import { api } from '../services/api';

const INSIDER_ROOT = 'perubahan_kepemilikan_idx/5%25/';
const KEPEMILIKAN_ROOT = 'perubahan_kepemilikan_idx/kepemilikan/';

function buildBlobUrl(path: string, containerUrl: string, sasToken: string): string {
  const isDev = (import.meta as any).env?.DEV || false;
  return isDev
    ? `/azure-blob-proxy/trading-data-v2/${path}?${sasToken}`
    : `${containerUrl}/${path}?${sasToken}`;
}

function parseCSVLine(text: string): string[] {
  let inQuotes = false;
  let currentVal = '';
  const result: string[] = [];
  
  for (let i = 0; i < text.length; i++) {
    const char = text[i];
    
    if (char === '"' && text[i + 1] === '"') {
      currentVal += '"';
      i++;
    } else if (char === '"') {
      inQuotes = !inQuotes;
    } else if (char === ',' && !inQuotes) {
      result.push(currentVal.trim());
      currentVal = '';
    } else {
      currentVal += char;
    }
  }
  result.push(currentVal.trim());
  return result;
}

export async function fetchInsider5pctData(date: string): Promise<{ data: any[] }> {
  try {
    const tokenRes = await api.getStockMetadataSasToken();
    if (!tokenRes.success || !tokenRes.data) throw new Error(tokenRes.error || 'Failed to get SAS token');
    
    const { containerUrl, sasToken } = tokenRes.data;
    const dateFormatted = date.replace(/-/g, '');
    const blobName = `${INSIDER_ROOT}${dateFormatted}.csv`;
    const url = buildBlobUrl(blobName, containerUrl, sasToken);
    
    const res = await fetch(url);
    if (!res.ok) {
      if (res.status === 404) return { data: [] };
      throw new Error(`HTTP error! status: ${res.status}`);
    }
    
    const text = await res.text();
    const lines = text.split(/\r?\n/).filter(line => line.trim() !== '');
    if (lines.length < 2) return { data: [] };
    
    const headers = parseCSVLine(lines[0]);
    const data = [];
    
    for (let i = 1; i < lines.length; i++) {
      const values = parseCSVLine(lines[i]);
      const obj: Record<string, string | number> = {};
      headers.forEach((h, idx) => {
        let val = values[idx] || '';
        obj[h] = val;
      });
      data.push(obj);
    }
    
    return { data };
  } catch (err) {
    console.error('Error fetching insider 5% data:', err);
    throw err;
  }
}

const MONTH_MAP: Record<string, string> = {
  'januari': '01',
  'februari': '02',
  'maret': '03',
  'april': '04',
  'mei': '05',
  'juni': '06',
  'juli': '07',
  'agustus': '08',
  'september': '09',
  'oktober': '10',
  'november': '11',
  'desember': '12'
};

function parseIndonesianDate(dateStr: string): string {
  const parts = dateStr.trim().split(/\s+/);
  if (parts.length === 3) {
    let day = parts[0];
    if (day.length === 1) day = '0' + day;
    const monthStr = parts[1].toLowerCase();
    const month = MONTH_MAP[monthStr] || '01';
    const year = parts[2];
    return `${year}-${month}-${day}`;
  }
  return dateStr;
}

async function fetchAndParseLaporanCSV(url: string): Promise<any[]> {
  const res = await fetch(url);
  if (!res.ok) {
    if (res.status === 404) return [];
    throw new Error(`HTTP error! status: ${res.status}`);
  }
  
  const text = await res.text();
  const lines = text.split(/\r?\n/).filter(line => line.trim() !== '');
  if (lines.length < 2) return [];
  
  const headers = parseCSVLine(lines[0]);
  const data = [];
  
  for (let i = 1; i < lines.length; i++) {
    const values = parseCSVLine(lines[i]);
    const obj: Record<string, string | number> = {};
    headers.forEach((h, idx) => {
      let val = values[idx] || '';
      if (h === 'Kode Emiten') {
        obj['Kode Efek'] = val;
      } else {
        obj[h] = val;
      }
    });
    // Fill in defaults for missing columns
    obj['Nama Rekening Efek'] = '-';
    obj['_source'] = 'emiten'; // add source indicator
    
    // format date if needed, assuming Tanggal is YYYY-MM-DD, YYYYMMDD, or Indonesian Date format
    if (obj['Tanggal'] && typeof obj['Tanggal'] === 'string') {
       const d = obj['Tanggal'].trim();
       if (d.length === 8 && !d.includes('-') && !d.includes(' ')) {
         obj['_date'] = `${d.substring(0,4)}-${d.substring(4,6)}-${d.substring(6,8)}`;
       } else if (d.includes('-')) {
         obj['_date'] = d;
       } else {
         // Parse Indonesian Date
         obj['_date'] = parseIndonesianDate(d);
       }
    }
    
    data.push(obj);
  }
  
  return data;
}

export async function fetchLaporanKepemilikanData(): Promise<{ data: any[] }> {
  try {
    const tokenRes = await api.getStockMetadataSasToken();
    if (!tokenRes.success || !tokenRes.data) throw new Error(tokenRes.error || 'Failed to get SAS token');
    
    const { containerUrl, sasToken } = tokenRes.data;
    const urlMain = buildBlobUrl(`${KEPEMILIKAN_ROOT}laporan_kepemilikan.csv`, containerUrl, sasToken);
    const urlTemp = buildBlobUrl(`${KEPEMILIKAN_ROOT}laporan_kepemilikan_temp.csv`, containerUrl, sasToken);
    
    const [resMain, resTemp] = await Promise.allSettled([
      fetchAndParseLaporanCSV(urlMain),
      fetchAndParseLaporanCSV(urlTemp)
    ]);
    
    const dataMain = resMain.status === 'fulfilled' ? resMain.value : [];
    const dataTemp = resTemp.status === 'fulfilled' ? resTemp.value : [];
    
    const combined = [...dataMain, ...dataTemp];
    
    // Deduplicate main vs temp. Satu pemegang bisa punya beberapa laporan di hari yang sama,
    // jadi kuncinya nomor laporan (LK/...) atau saldo sebelum/sesudah — bukan cuma tanggal+nama.
    const seen = new Set<string>();
    const deduplicated = combined.filter(row => {
      const key = row['Nomor'] || [
        row._date ?? '',
        row['Kode Efek'] ?? '',
        row['Nama Pemegang Saham'] ?? '',
        row['Jumlah Saham (Hari Sebelumnya)'] ?? '',
        row['Jumlah Saham (Hari Ini)'] ?? ''
      ].join('|');
      
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
    
    return { data: deduplicated };
  } catch (err) {
    console.error('Error fetching laporan kepemilikan data:', err);
    return { data: [] };
  }
}

export async function fetchInsider5pctLatestDate(): Promise<string | null> {
  try {
    const tokenRes = await api.getStockMetadataSasToken();
    if (!tokenRes.success || !tokenRes.data) throw new Error(tokenRes.error || 'Failed to get SAS token');
    
    const { containerUrl, sasToken } = tokenRes.data;
    const isDev = (import.meta as any).env?.DEV || false;
    const baseUrl = isDev ? `/azure-blob-proxy/trading-data-v2` : containerUrl;
    
    const prefix = encodeURIComponent('perubahan_kepemilikan_idx/5%/');
    const listUrl = `${baseUrl}?restype=container&comp=list&prefix=${prefix}&${sasToken}`;
    
    const res = await fetch(listUrl);
    if (!res.ok) throw new Error(`List failed: ${res.status}`);
    const text = await res.text();
    
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(text, "text/xml");
    const names = Array.from(xmlDoc.getElementsByTagName("Name")).map(el => el.textContent);
    
    const csvs = names.filter(n => n?.endsWith('.csv')).sort();
    if (csvs.length === 0) return null;
    
    const latestCsv = csvs[csvs.length - 1];
    const filename = latestCsv!.split('/').pop();
    if (filename) {
      const dateStr = filename.replace('.csv', '');
      // Format YYYYMMDD to YYYY-MM-DD
      if (dateStr.length === 8) {
        return `${dateStr.substring(0,4)}-${dateStr.substring(4,6)}-${dateStr.substring(6,8)}`;
      }
    }
    return null;
  } catch (err) {
    console.error('Error fetching latest insider date:', err);
    return null;
  }
}
